import type { Document, ExtraDataValue } from "@/api/models"
import type { M365Type } from "@/utils/citation-utils"

import { formatM365Citation, getM365Type } from "@/utils/citation-utils"

export type M365CitationDisplay = {
  type: M365Type | null
  title: string
  metadata: string
  url: string
  identifier: string
}

export type M365TypeLabelResolver = (type: M365Type | null) => string

function getExtraDataValue(
  extraData: Record<string, ExtraDataValue> | undefined,
  key: string
): ExtraDataValue | undefined {
  return extraData?.[key]
}

function getStringExtraData(
  extraData: Record<string, ExtraDataValue> | undefined,
  key: string
): string | undefined {
  const value = getExtraDataValue(extraData, key)
  return typeof value === "string" ? value : undefined
}

function getNumberExtraData(
  extraData: Record<string, ExtraDataValue> | undefined,
  key: string
): number | undefined {
  const value = getExtraDataValue(extraData, key)
  return typeof value === "number" ? value : undefined
}

function formatLocalDate(date: string): string | undefined {
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return undefined
  return parsed.toLocaleDateString()
}

export function getM365TypeLabel(
  type: M365Type | null,
  labelResolver?: M365TypeLabelResolver
): string {
  if (labelResolver) return labelResolver(type)

  switch (type) {
    case "email":
      return "Email"
    case "event":
      return "Event"
    case "file":
      return "File"
    case "teams":
      return "Teams"
    default:
      return "Microsoft 365"
  }
}

export function resolveM365CitationDisplay(
  citation: string,
  documents: [string, Document][] = []
): M365CitationDisplay {
  const type = getM365Type(citation)
  const { displayName, identifier } = formatM365Citation(citation)
  const document = documents.find(([citationKey]) => citationKey === citation)?.[1]
  const extraData = document?.extra_data

  const url = getStringExtraData(extraData, "url") ?? ""
  let title = type ? displayName : identifier || displayName
  let metadata = ""

  const subject = getStringExtraData(extraData, "subject")
  const filename = getStringExtraData(extraData, "filename")
  if (subject) {
    title = subject
  } else if (filename) {
    title = filename
  }

  if (type === "email") {
    const sender = getStringExtraData(extraData, "sender")
    const date = getStringExtraData(extraData, "date")
    if (sender) {
      metadata = `From: ${sender}`
      const formattedDate = date ? formatLocalDate(date) : undefined
      if (formattedDate) {
        metadata += ` • ${formattedDate}`
      }
    }
  } else if (type === "event") {
    const start = getStringExtraData(extraData, "start")
    const location = getStringExtraData(extraData, "location")
    if (start) {
      const parsedStart = new Date(start)
      if (!Number.isNaN(parsedStart.getTime())) {
        metadata = parsedStart.toLocaleString()
      }
    }
    if (location) {
      metadata = metadata ? `${metadata} • ${location}` : location
    }
  } else if (type === "teams") {
    metadata = getStringExtraData(extraData, "participants") ?? ""
  } else if (type === "file") {
    const size = getNumberExtraData(extraData, "size")
    if (size && size > 0) {
      const sizeKb = Math.round(size / 1024)
      metadata = sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB`
    }
  }

  return {
    type,
    title,
    metadata,
    url,
    identifier,
  }
}
